import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Plus, Trash2, Pencil, Loader2, Save, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import api, { pdvApi } from '../lib/api';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import HaccpConsultantHeader from '../components/HaccpConsultantHeader';

const emptyItem = () => ({ prodotto: '', presente: [], contaminazione_crociata: [], sito_produttivo: [], ingredienti_text: '', note: '' });
const emptyForm = () => ({ pdv_id: '', titolo: '', data_compilazione: new Date().toISOString().slice(0, 10), prodotti: [emptyItem()], note: '' });

export default function HaccpAllergensPage() {
  const { currentPdvId } = useCurrentPdv();
  const [allergeni, setAllergeni] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, p, r] = await Promise.all([api.get('/haccp-allergens/list-allergeni'), pdvApi.getAll(), api.get('/haccp-allergens')]);
      setAllergeni(a.data); setPdvList(p.data); setRecords(r.data);
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    const f = emptyForm();
    if (currentPdvId && pdvList.some(p => p.id === currentPdvId)) {
      f.pdv_id = currentPdvId;
    } else if (pdvList.length === 1) {
      f.pdv_id = pdvList[0].id;
    }
    setForm(f); setDialog(true);
  };
  const openEdit = async (rec) => {
    try {
      const full = await api.get(`/haccp-allergens/${rec.id}`);
      const d = full.data;
      setForm({
        pdv_id: d.pdv_id || '', titolo: d.titolo || '',
        data_compilazione: d.data_compilazione || '',
        prodotti: (d.prodotti || [emptyItem()]).map(p => ({ ...emptyItem(), ...p })),
        note: d.note || '',
      });
      setEditingId(rec.id); setDialog(true);
    } catch (e) { toast.error('Errore'); }
  };
  const handleSave = async () => {
    if (!form.pdv_id) { toast.error('Seleziona UL'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editingId) await api.put(`/haccp-allergens/${editingId}`, payload);
      else await api.post('/haccp-allergens', payload);
      toast.success(editingId ? 'Aggiornato' : 'Creato');
      setDialog(false); load();
    } catch (e) { toast.error('Errore'); }
    finally { setSaving(false); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare?')) return;
    try { await api.delete(`/haccp-allergens/${id}`); toast.success('Eliminato'); load(); }
    catch (e) { toast.error('Errore'); }
  };

  const addItem = () => setForm(p => ({ ...p, prodotti: [...p.prodotti, emptyItem()] }));
  const removeItem = (i) => setForm(p => ({ ...p, prodotti: p.prodotti.filter((_, idx) => idx !== i) }));
  const setItem = (i, patch) => setForm(p => { const n = [...p.prodotti]; n[i] = { ...n[i], ...patch }; return { ...p, prodotti: n }; });

  // v1.3.39 — rileva allergeni dal testo etichetta e auto-compila SI/POSSIBILE
  const [detecting, setDetecting] = useState(null);
  const detectItem = async (i) => {
    const text = (form.prodotti[i].ingredienti_text || '').trim();
    if (!text) { toast.error('Inserisci ingredienti/etichetta per il rilevamento'); return; }
    setDetecting(i);
    try {
      const r = await api.post('/haccp-allergens/detect', { text });
      setItem(i, {
        presente: r.data.presente || [],
        contaminazione_crociata: r.data.contaminazione_crociata || [],
        sito_produttivo: r.data.sito_produttivo || [],
      });
      toast.success(`Rilevati ${(r.data.presente || []).length} allergeni (SI) → cross-contamination e sito: POSSIBILE`);
    } catch { toast.error('Rilevamento fallito'); }
    finally { setDetecting(null); }
  };
  // Toggle cella matrice. Su "presente": ON aggiunge anche cross+sito (POSSIBILE); OFF li rimuove.
  const toggleMatrix = (i, allergen, col) => {
    const it = form.prodotti[i];
    const has = it[col].includes(allergen);
    let patch = {};
    if (col === 'presente') {
      if (has) {
        patch = {
          presente: it.presente.filter(x => x !== allergen),
          contaminazione_crociata: it.contaminazione_crociata.filter(x => x !== allergen),
          sito_produttivo: it.sito_produttivo.filter(x => x !== allergen),
        };
      } else {
        patch = {
          presente: [...it.presente, allergen],
          contaminazione_crociata: it.contaminazione_crociata.includes(allergen) ? it.contaminazione_crociata : [...it.contaminazione_crociata, allergen],
          sito_produttivo: it.sito_produttivo.includes(allergen) ? it.sito_produttivo : [...it.sito_produttivo, allergen],
        };
      }
    } else {
      patch = { [col]: has ? it[col].filter(x => x !== allergen) : [...it[col], allergen] };
    }
    setItem(i, patch);
  };

  const pdvName = (id) => pdvList.find(p => p.id === id)?.name || '—';
  const selectedPdv = pdvList.find(p => p.id === form.pdv_id);

  const scaricaListaAllergeni = () => {
    if (!allergeni.length) { toast.error('Lista allergeni non disponibile'); return; }
    const content = 'Lista allergeni UE — Reg. 1169/2011\n' +
      '====================================\n\n' +
      allergeni.map((a, i) => `${String(i + 1).padStart(2, '0')}. ${a}`).join('\n') + '\n';
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'Lista_Allergeni_UE.txt'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6" data-testid="haccp-allergens-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><AlertTriangle className="w-7 h-7 text-rose-500" /> HACCP — Allergeni</h1>
          <p className="text-gray-600 mt-1">Matrice prodotti e allergeni UE (Reg. 1169/2011). Indica presenza e rischio di contaminazione crociata.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={scaricaListaAllergeni} data-testid="download-allergens-list-btn">
            <Download className="w-4 h-4 mr-1" /> Scarica lista allergeni
          </Button>
          <Button onClick={openCreate} data-testid="new-allergen-btn"><Plus className="w-4 h-4 mr-1" /> Nuova Matrice</Button>
        </div>
      </div>

      {loading ? <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        : records.length === 0 ? <Card><CardContent className="py-10 text-center text-gray-500">Nessuna matrice.</CardContent></Card>
        : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {records.map((r) => (
              <Card key={r.id}>
                <CardHeader><CardTitle className="text-base">{r.titolo || `Matrice ${pdvName(r.pdv_id)}`}</CardTitle></CardHeader>
                <CardContent>
                  <Badge variant="outline" className="mb-2">{pdvName(r.pdv_id)}</Badge>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>📅 {r.data_compilazione}</div>
                    <div>🍽️ {(r.prodotti || []).length} prodotti</div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(r.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="allergens-form-dialog">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica Matrice Allergeni' : 'Nuova Matrice Allergeni'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <HaccpConsultantHeader pdv={selectedPdv} dateLabel={form.data_compilazione} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label>Unità Locale *</Label>
                <Select value={form.pdv_id} onValueChange={(v) => setForm({ ...form, pdv_id: v })}>
                  <SelectTrigger data-testid="ul-select"><SelectValue placeholder="Seleziona UL" /></SelectTrigger>
                  <SelectContent>{pdvList.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Titolo</Label><Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} /></div>
              <div><Label>Data</Label><Input type="date" value={form.data_compilazione} onChange={(e) => setForm({ ...form, data_compilazione: e.target.value })} /></div>
            </div>
            <div className="border-t pt-3">
              <div className="flex justify-between mb-2">
                <h3 className="font-semibold">Prodotti ({form.prodotti.length})</h3>
                <Button size="sm" variant="outline" onClick={addItem}><Plus className="w-4 h-4 mr-1" /> Aggiungi</Button>
              </div>
              <div className="space-y-3">
                {form.prodotti.map((it, i) => (
                  <Card key={i} className="border-l-4 border-l-rose-300">
                    <CardContent className="py-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Input value={it.prodotto} onChange={(e) => setItem(i, { prodotto: e.target.value })} placeholder={`Prodotto #${i + 1} (es. Pasta al ragù)`} className="font-semibold" data-testid={`prodotto-${i}`} />
                        <Button size="sm" variant="ghost" onClick={() => removeItem(i)} disabled={form.prodotti.length <= 1}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                      <div>
                        <Label className="text-xs">Ingredienti / etichetta (per rilevamento automatico)</Label>
                        <div className="flex gap-2 mt-1">
                          <Textarea rows={2} value={it.ingredienti_text || ''} onChange={(e) => setItem(i, { ingredienti_text: e.target.value })}
                            placeholder="Incolla qui la lista ingredienti dall'etichetta…" data-testid={`ingredienti-text-${i}`} />
                          <Button size="sm" variant="outline" onClick={() => detectItem(i)} disabled={detecting === i}
                            className="whitespace-nowrap self-start" data-testid={`detect-allergeni-${i}`}>
                            {detecting === i ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Rileva allergeni'}
                          </Button>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1">Dove <strong>Presente = SI</strong>, il sistema spunta automaticamente <strong>POSSIBILE</strong> in contaminazione crociata e presenza nel sito produttivo. Modificabile a mano.</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border border-gray-200" data-testid={`allergen-matrix-${i}`}>
                          <thead>
                            <tr className="bg-gray-50 text-gray-700">
                              <th className="text-left px-2 py-1 border-b">Allergene (14 UE)</th>
                              <th className="px-2 py-1 border-b">Presente</th>
                              <th className="px-2 py-1 border-b">Contaminazione crociata</th>
                              <th className="px-2 py-1 border-b">Presenza nel sito produttivo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allergeni.map((a) => {
                              const pres = it.presente.includes(a);
                              const cross = it.contaminazione_crociata.includes(a);
                              const sito = it.sito_produttivo.includes(a);
                              const cell = (active, activeCls, onLabel) => `w-full px-2 py-1 text-xs rounded border transition-colors ${active ? activeCls : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50'}`;
                              return (
                                <tr key={a} className="border-b last:border-0">
                                  <td className={`px-2 py-1 ${pres ? 'font-bold uppercase text-rose-700' : ''}`}>{a}</td>
                                  <td className="px-1 py-1 text-center">
                                    <button type="button" onClick={() => toggleMatrix(i, a, 'presente')}
                                      className={cell(pres, 'bg-rose-500 text-white border-rose-500')} data-testid={`pres-${i}-${a}`}>
                                      {pres ? 'SI' : '—'}
                                    </button>
                                  </td>
                                  <td className="px-1 py-1 text-center">
                                    <button type="button" onClick={() => toggleMatrix(i, a, 'contaminazione_crociata')}
                                      className={cell(cross, 'bg-amber-400 text-white border-amber-400')} data-testid={`cross-${i}-${a}`}>
                                      {cross ? 'POSSIBILE' : '—'}
                                    </button>
                                  </td>
                                  <td className="px-1 py-1 text-center">
                                    <button type="button" onClick={() => toggleMatrix(i, a, 'sito_produttivo')}
                                      className={cell(sito, 'bg-amber-400 text-white border-amber-400')} data-testid={`sito-${i}-${a}`}>
                                      {sito ? 'POSSIBILE' : '—'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <Input value={it.note} onChange={(e) => setItem(i, { note: e.target.value })} placeholder="Note" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            <div><Label>Note generali</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
